/**
 * Filiation des SKILLCARDS (migrations 0275 → 0283 N-N) — PUR, testé.
 *
 * Une carte dérivée B (« double distributivité ») est la suite plus avancée
 * d'une ou plusieurs cartes mères (table `card_relations`) ; sa rareté vaut
 * toujours celle de la plus rare des mères + 1 étoile (imposée par le serveur).
 * `cards.parent_card_id` reste la mère choisie à la création (compat 0275).
 */

import { cardRarityFromParents, nextCardRarity, type CardRarity } from '@jeraisonne/db/cards';
import type { CardInput, CardRelationRow, CardRow } from './api';

/** Arête de filiation (table `card_relations`, migration 0283). */
export type CardEdge = Pick<CardRelationRow, 'parent_card_id' | 'child_card_id'>;

/** Mères directes de `cardId`. */
export function parentIdsOf(edges: readonly CardEdge[], cardId: string): string[] {
  return edges.filter((e) => e.child_card_id === cardId).map((e) => e.parent_card_id);
}

/** Dérivées directes de `cardId`. */
export function childIdsOf(edges: readonly CardEdge[], cardId: string): string[] {
  return edges.filter((e) => e.parent_card_id === cardId).map((e) => e.child_card_id);
}

/** Toutes les cartes qui descendent de `cardId` (enfants, petits-enfants…). */
export function descendantIds(edges: readonly CardEdge[], cardId: string): Set<string> {
  const out = new Set<string>();
  const queue = [cardId];
  while (queue.length) {
    const id = queue.shift()!;
    for (const childId of childIdsOf(edges, id)) {
      if (!out.has(childId)) {
        out.add(childId);
        queue.push(childId);
      }
    }
  }
  return out;
}

/**
 * Cartes qui peuvent devenir une (nouvelle) mère de `cardId` : ni elle-même,
 * ni l'une de ses descendantes (pas de cycle), ni déjà sa mère, ni archivée.
 * Même matière d'abord, puis par titre.
 */
export function parentCandidates<T extends Pick<CardRow, 'id' | 'title' | 'subject' | 'status'>>(
  cards: readonly T[],
  edges: readonly CardEdge[],
  cardId: string | null,
  subject: string,
): T[] {
  const excluded = cardId ? descendantIds(edges, cardId) : new Set<string>();
  if (cardId) {
    excluded.add(cardId);
    for (const id of parentIdsOf(edges, cardId)) excluded.add(id);
  }
  return cards
    .filter((c) => !excluded.has(c.id) && c.status !== 'archived')
    .sort(
      (a, b) =>
        Number(b.subject === subject) - Number(a.subject === subject) ||
        a.title.localeCompare(b.title, 'fr'),
    );
}

/**
 * Rareté imposée par TOUTES les mères de `cardId` (plus rare + 1 étoile), ou
 * null sans mère. Affichage seulement : le serveur reste seul juge.
 */
export function rarityFromRelations(
  cards: readonly Pick<CardRow, 'id' | 'rarity'>[],
  edges: readonly CardEdge[],
  cardId: string,
): CardRarity | null {
  const ids = new Set(parentIdsOf(edges, cardId));
  return cardRarityFromParents(cards.filter((c) => ids.has(c.id)).map((c) => c.rarity));
}

/** Rareté imposée par la parente (affichage ; le serveur reste seul juge). */
export function inheritedRarity(
  parent: Pick<CardRow, 'rarity'> | null | undefined,
): CardRarity | null {
  return parent ? nextCardRarity(parent.rarity) : null;
}

/** Formulaire prérempli pour « Créer une carte dérivée » depuis la carte A. */
export function derivedCardInput(parent: CardRow, empty: CardInput): CardInput {
  return {
    ...empty,
    card_type: parent.card_type,
    subject: parent.subject,
    topic: parent.topic,
    domain_label: parent.domain_label,
    levels: [...parent.levels],
    xp_max: parent.xp_max,
    difficulty: parent.difficulty,
    importance: parent.importance,
    color_theme: parent.color_theme,
    rarity: nextCardRarity(parent.rarity),
    parent_card_id: parent.id,
  };
}
